import { ENUM_MEMBERSHIP_TYPE, getMembershipType, MEMBERSHIP_FALLBACK } from 'constants/Enums/premium';
import { useAuth } from 'context/Auth';
import { useRouter } from 'next/router';
import { isPrd, PREMIUM_LANDING_PAGE_URL } from 'sysconfig';
import { formatDate3 } from 'utils/FormatDate';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const PREVIEW_EXPIRED_DAYS = 30;

/** Field `membership` của customer, BE chỉ set khi gói còn hiệu lực */
interface IMembershipView {
  type?: ENUM_MEMBERSHIP_TYPE;
  name?: string;
  shortName?: string;
  expiredAt?: string;
}

export interface IUsePremium {
  /** Gói thành viên của khách */
  membershipType: ENUM_MEMBERSHIP_TYPE;
  /** Tên gói để hiển thị, rỗng nếu là khách thường */
  membershipName: string;
  /** Tên ngắn của gói cho badge */
  membershipShortName: string;
  /** Khách đang có gói trả phí còn hiệu lực, không phân biệt gói nào */
  hasActiveMembership: boolean;
  /** Ngày hết hạn gói dạng dd/MM/yyyy, rỗng nếu BE chưa trả về */
  expiredAtText: string;
  /** Số ngày còn lại của gói */
  remainingDays: number;
  landingPageUrl: string;
  /** Mở landing page đăng ký gói của MKT ở tab mới */
  openLandingPage: () => void;
}

/**
 * Luồng UI trên cart theo gói của khách:
 * - PREMIUM: ẩn banner + block "Giá với Premium" (quyền lợi 2% hiện dưới dạng
 *   voucher PREMIUM trong Tổng giảm giá, do BE trả về).
 * - NORMAL: hiện banner + block "Giá với Premium" kèm CTA đăng ký.
 *
 * Đừng dùng field `customerType` - UserService.getAccount đang ghi đè field này
 * bằng customer type của Insider.
 */
const usePremium = (): IUsePremium => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { user }: any = useAuth();
  const router = useRouter();

  // Xem trước 2 luồng UI ở non-prd: /cart?premium=1 hoặc /cart?premium=0
  const previewParam = router?.query?.premium;
  const isPreviewMode = !isPrd && typeof previewParam === 'string';

  // BE set `membership` khi gói còn hiệu lực. getMembershipType là fallback cho
  // môi trường BE chưa deploy.
  const membership: IMembershipView | undefined = user?.membership;
  const membershipType = isPreviewMode
    ? (previewParam === '1' && ENUM_MEMBERSHIP_TYPE.PREMIUM) || ENUM_MEMBERSHIP_TYPE.NORMAL
    : membership?.type || getMembershipType(user);
  const hasActiveMembership = membershipType !== ENUM_MEMBERSHIP_TYPE.NORMAL;
  const fallback = MEMBERSHIP_FALLBACK[membershipType] || MEMBERSHIP_FALLBACK[ENUM_MEMBERSHIP_TYPE.NORMAL];

  const openLandingPage = () => {
    if (!PREMIUM_LANDING_PAGE_URL) return;
    window.open(PREMIUM_LANDING_PAGE_URL, '_blank', 'noopener,noreferrer');
  };

  // Preview ở non-prd chưa có data thật thì tự sinh ngày hết hạn để xem được UI
  const getExpiredAt = () => {
    if (!hasActiveMembership) return null;
    const expiredAt = membership?.expiredAt || user?.membershipExpiredAt;
    if (expiredAt) return new Date(expiredAt);
    return isPreviewMode ? new Date(Date.now() + PREVIEW_EXPIRED_DAYS * MS_PER_DAY) : null;
  };

  const expiredAt = getExpiredAt();
  const remainingDays = expiredAt ? Math.max(0, Math.ceil((expiredAt.getTime() - Date.now()) / MS_PER_DAY)) : 0;

  return {
    membershipType,
    membershipName: membership?.name || fallback.name,
    membershipShortName: membership?.shortName || fallback.shortName,
    hasActiveMembership,
    expiredAtText: expiredAt ? formatDate3(expiredAt.toISOString(), true) : '',
    remainingDays,
    landingPageUrl: PREMIUM_LANDING_PAGE_URL,
    openLandingPage,
  };
};

export default usePremium;
